import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

const homepageFallbacks = new Map();

class ProductRecommendations extends Component {
  #observer;
  #mutations;
  #activeFetch;
  #cache = new Map();
  #visible = false;
  #lastKey = '';
  #fallbackHTML = '';

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.nhPersonalized === 'true') {
      const id = this.dataset.sectionId;
      if (!homepageFallbacks.has(id) && this.dataset.recommendationsPerformed !== 'true') {
        homepageFallbacks.set(id, this.innerHTML);
      }
      this.#fallbackHTML = homepageFallbacks.get(id) || '';
    }
    this.#observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this.#visible = true;
      this.#observer.disconnect();
      this.#loadRecommendations();
    }, { rootMargin: '0px 0px 400px 0px' });
    this.#observer.observe(this);
    this.#mutations = new MutationObserver(() => {
      this.#lastKey = '';
      if (this.#visible) this.#loadRecommendations();
    });
    this.#mutations.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-intent', 'data-section-id', 'data-url']
    });
    document.addEventListener('nh:preferences-changed', this.#onConsent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    this.#activeFetch?.abort();
    document.removeEventListener('nh:preferences-changed', this.#onConsent);
  }

  #onConsent = () => {
    if (this.dataset.nhPersonalized !== 'true') return;
    this.#activeFetch?.abort();
    this.#lastKey = '';
    this.dataset.recommendationsPerformed = 'false';
    if (this.#fallbackHTML) this.innerHTML = this.#fallbackHTML;
    if (this.#visible) this.#loadRecommendations();
  };

  async #loadRecommendations() {
    const personalized = this.dataset.nhPersonalized === 'true';
    let productId = this.dataset.productId;
    let intent = this.dataset.intent || 'related';
    const sectionId = this.dataset.sectionId;
    if (personalized) {
      if (window.Shopify?.designMode || this.dataset.recommendationsPerformed === 'true') return;
      await window.NHBrowsing?.ready;
      if (!this.isConnected) return;
      productId = window.NHBrowsing?.getLastProduct();
      intent = 'related';
      if (!productId) return; // Keep the selected fallback collection.
    } else if (this.dataset.recommendationsPerformed === 'true') return;
    if (!productId || !sectionId || !this.dataset.url) return;

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent);
    const key = url.toString();
    if (this.#lastKey === key) return;
    this.#lastKey = key;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    try {
      let html = this.#cache.get(key);
      if (!html) {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Recommendations: ${response.status}`);
        html = await response.text();
      }
      if (controller.signal.aborted || !this.isConnected) return;
      if (personalized && window.NHBrowsing?.getLastProduct() !== productId) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const result = parsed.getElementById(`product-recommendations-${sectionId}`);
      if (!result || result.dataset.recommendationsPerformed !== 'true') {
        throw new Error('Recommendation section not available');
      }
      if (personalized && !result.querySelector('[data-has-recommendations="true"]')) return;
      this.#cache.set(key, html);
      this.dataset.recommendationsPerformed = 'true';
      morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#lastKey = '';
      console.warn('Product recommendations:', error.message);
      // On the homepage retain the collection instead of hiding the section.
      if (!personalized && !window.Shopify?.designMode) this.classList.add('hidden');
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
